import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { formatNumber, formatPercent } from "../../../utils/numberFormat";

/**
 * SE Ranking's evolution chart, shared by the client metrics screen and the
 * monthly report so both read the same series the same way.
 */

// A null is not a zero: an unknown value reads as an em dash.
const orDash = (value, formatter) =>
  value === null || value === undefined ? "—" : formatter(value);

export const formatHistoryDay = (isoDate) => {
  try {
    return format(parseISO(isoDate), "d MMM", { locale: es });
  } catch {
    return isoDate;
  }
};

/**
 * The series the "Evolución" chart can plot. Position runs the other way
 * (1 is best), so its axis is reversed and shown, and its area fills from the
 * line down to the worst value instead of up to the top.
 */
export const EVOLUTION_METRICS = {
  visibility: {
    label: "Visibilidad",
    historyKey: "visibility_history",
    gradientId: "seRankingVisibility",
    reversed: false,
    tooltip: (value) => (
      <>
        Visibilidad: <strong>{formatPercent(value ?? 0)}</strong>
      </>
    ),
    emptyMessage: (source) => `${source} todavía no tiene histórico de visibilidad para este rango.`,
  },
  position: {
    label: "Posición promedio",
    historyKey: "avg_position_history",
    gradientId: "seRankingAvgPosition",
    reversed: true,
    tooltip: (value) => (
      <>
        Posición: <strong>{orDash(value, formatNumber)}</strong>
      </>
    ),
    emptyMessage: (source) => `${source} todavía no tiene histórico de posición promedio para este rango.`,
  },
  // One keyword's own history, under an expanded ranking row. It is not one
  // of EVOLUTION_METRIC_TABS: nothing lets the user switch to it, the
  // expanded row asks for it by name.
  keywordPosition: {
    label: "Posición",
    gradientId: "seRankingKeywordPosition",
    reversed: true,
    // `value` is what gets plotted, which a day without a ranking pushes just
    // past the tracked range; `position` is what SE Ranking reported.
    tooltip: (value, point) =>
      point?.position === null || point?.position === undefined ? (
        <>Sin posicionar (fuera del top 100)</>
      ) : (
        <>
          Posición: <strong>{formatNumber(point.position)}</strong>
        </>
      ),
    emptyMessage: (source) => `${source} todavía no tiene histórico de esta keyword.`,
  },
};

/** The metrics the "Evolución" card offers as tabs, in order. */
export const EVOLUTION_METRIC_TABS = ["visibility", "position"];

/**
 * Where a day without a ranking is plotted.
 *
 * `pos: 0` is SE Ranking's "outside the tracked range", and it covers two
 * cases the API gives no way to tell apart: the keyword really left the top
 * 100, and SE Ranking ran no check that day (recognisable only because every
 * keyword of the project reads 0 on the same date). Leaving a gap for both
 * made the chart look broken, so they are drawn the way SE Ranking's own
 * chart draws them — a dive just past the bottom of the range — while the
 * tooltip still names the day for what it is.
 */
export const OUT_OF_RANGE_POSITION = 101;

/** Tooltip for the evolution chart, styled like the traffic chart's. */
function HistoryTooltip({ active, payload, borderColor, metric }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        padding: 1.5,
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
        {formatHistoryDay(point.date)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {(EVOLUTION_METRICS[metric] || EVOLUTION_METRICS.visibility).tooltip(
          point.value,
          point
        )}
      </Typography>
    </Box>
  );
}

HistoryTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  borderColor: PropTypes.string,
  metric: PropTypes.string,
};

export function VisibilityHistoryChart({ history, metric = "visibility", height = 220 }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const config = EVOLUTION_METRICS[metric] || EVOLUTION_METRICS.visibility;

  if (!history?.length) return null;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={history} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={config.gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.28} />
            <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={neutrals.gray[200]} vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={formatHistoryDay}
          stroke={neutrals.gray[500]}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: neutrals.gray[200] }}
          minTickGap={32}
        />
        {config.reversed ? (
          // Position 1 at the top, so climbing reads as going up; the axis is
          // visible because an unlabelled inverted scale would read backwards.
          <YAxis
            reversed
            allowDecimals={false}
            domain={[
              (min) => Math.max(1, Math.floor(min) - 2),
              (max) => Math.ceil(max) + 2,
            ]}
            width={36}
            stroke={neutrals.gray[500]}
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
          />
        ) : (
          <YAxis hide domain={[0, "auto"]} />
        )}
        <ChartTooltip
          content={<HistoryTooltip borderColor={neutrals.gray[200]} metric={metric} />}
          cursor={{ stroke: neutrals.gray[300], strokeDasharray: "3 3" }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={theme.palette.primary.main}
          strokeWidth={1.8}
          fill={`url(#${config.gradientId})`}
          // On a reversed axis the default base is the top edge; fill down to
          // the worst position instead.
          baseValue={config.reversed ? "dataMax" : undefined}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

VisibilityHistoryChart.propTypes = {
  history: PropTypes.array,
  metric: PropTypes.oneOf(["visibility", "position"]),
  height: PropTypes.number,
};

export default VisibilityHistoryChart;
